/**
 * PHARMAKON — Cross-module X · XWP-19 — French copy of the sale remise (brief §9, §9.1;
 * 02_DESIGN §4.2) : the « Remise » field of the Caisse libre window with its « % | DA » unit, the
 * live effect, the ceiling hints, the refusals, the labels the receipt and the sale detail print, and
 * the « Remise totale du jour » tile of the dashboards.
 *
 * Copy only — no business value. The ceiling comes from the server référentiel
 * (`GET /ventes/referentiel`, XMD-02-09) and is passed in; the maths are the shared pricing engine
 * (`@pharmakon/shared/pricing/remise-vente.js`). The refusal wording mirrors the server's
 * `MESSAGES_REMISE_VENTE` (apps/server/src/config/remise-vente.config.js) so the preview and the
 * server answer read the same.
 */
import { ERREUR_REMISE_VENTE, MODE_REMISE_VENTE } from "@pharmakon/shared/pricing/remise-vente.js";

export const REMISE_VENTE_COPIE = Object.freeze({
  legende: "Remise",
  /** Accessible name of the « % | DA » unit control. */
  unite_label: "Unité de la remise",
  modes: Object.freeze({
    [MODE_REMISE_VENTE.POURCENTAGE]: Object.freeze({ label: "%", hint: "Remise en pourcentage du montant de la vente" }),
    [MODE_REMISE_VENTE.MONTANT]: Object.freeze({ label: "DA", hint: "Remise d'un montant fixe en dinars" }),
  }),
  /** Accessible name of the value field, per unit. */
  champ_label: Object.freeze({
    [MODE_REMISE_VENTE.POURCENTAGE]: "Remise en pourcentage",
    [MODE_REMISE_VENTE.MONTANT]: "Remise en dinars",
  }),
  aide_saisie: "Tapez « 5 % » ou « 500 DA ». Champ vide : Espace change d'unité. Entrée valide.",
  /** The live DZD effect under the field (« Remise : 500,00 DA »). */
  effet: (montant) => `Remise : ${montant}`,
  plafond_pct: (max) => `Max ${max} %`,
  plafond_montant: (montant, max) => `Max ${montant} (${max} % du montant)`,
  plafond_montant_sans_brut: (max) => `Max ${max} % du montant`,
  ajuster: (valeur) => `Ajuster à ${valeur}`,
  retirer: "Retirer la remise",
  /** F6 while the window is in Crédit mode (XD-16). */
  credit_sans_remise: "Pas de remise sur un crédit : la dette enregistre le prix plein.",
  /** The outcome dialog, when a remise is set: only the comptant outcome keeps it (XD-16). */
  hors_comptant: (montant) =>
    `La remise (${montant}) ne s'applique qu'à l'encaissement comptant : CHIFA, crédit et instance repartent du prix plein.`,
  /** F9 refused while the remise cannot be applied. */
  a_corriger: (message) => `Remise à corriger : ${message}`,
  /** Labels of a sale's remise (totals, receipt, sale detail). */
  libelle: "Remise",
  libelle_pct: (taux) => `Remise ${taux} %`,
  libelle_montant: "Remise (montant fixe)",
  erreurs: Object.freeze({
    [ERREUR_REMISE_VENTE.MODES_EXCLUSIFS]: () => "Choisissez une remise en % ou en DA, pas les deux.",
    [ERREUR_REMISE_VENTE.POURCENTAGE_HORS_BORNES]: ({ max }) => `Remise max : ${max} %.`,
    [ERREUR_REMISE_VENTE.MONTANT_NEGATIF]: () => "La remise doit être positive ou nulle.",
    [ERREUR_REMISE_VENTE.MONTANT_SUPERIEUR_BRUT]: ({ brut }) =>
      `La remise ne peut pas dépasser le montant de la vente (${brut}).`,
    [ERREUR_REMISE_VENTE.MONTANT_HORS_PLAFOND]: ({ max, plafond }) =>
      `Remise max : ${max} % du montant, soit ${plafond} pour cette vente.`,
  }),
});

/** « Remise totale du jour » (§9.1, XD-48 / XD-78) — the dashboards' tile. */
export const REMISE_JOUR_COPIE = Object.freeze({
  label: "Remise totale du jour",
  /** The tile's context line for the pharmacy (P / M) and for the caissier's own figure. */
  portee: Object.freeze({
    PHARMACIE: "Toutes les caisses",
    CAISSIER: "Vos ventes validées",
  }),
  par_mode: Object.freeze({
    [MODE_REMISE_VENTE.MONTANT]: (m) => `${m} en montant fixe`,
    [MODE_REMISE_VENTE.POURCENTAGE]: (m) => `${m} en pourcentage`,
  }),
  dont: (parts) => `dont ${parts.join(" · ")}`,
  aucune: "Aucune remise accordée aujourd'hui",
  ventes: (n) => `${n} vente${n > 1 ? "s" : ""} avec remise`,
  erreur: "Remises du jour indisponibles",
  reessayer: "Réessayer",
  par_caissier_titre: "Remises du jour par caissier",
  par_caissier_vide: "Aucune remise accordée aujourd'hui.",
  caissier_inconnu: "Caissier",
  colonnes: Object.freeze({ caissier: "Caissier", ventes: "Ventes", montant: "Remise" }),
});
